import { useState, useEffect, useRef } from 'react'

interface LazyVimeoEmbedProps {
  videoId: string
  startTime: number
  endTime: number
  className?: string
}

const LazyVimeoEmbed = ({
  videoId,
  startTime,
  endTime,
  className,
}: LazyVimeoEmbedProps) => {
  const [isInView, setIsInView] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const playerRef = useRef<any>(null)
  const [opacity, setOpacity] = useState(1)
  const fadeThreshold = 0.3

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isInView || !iframeRef.current) return

    let interval: NodeJS.Timeout
    let mounted = true

    // Dynamic import of Vimeo player - only loads when needed
    import('@vimeo/player').then(({ default: Player }) => {
      if (!mounted || !iframeRef.current) return

      const player = new Player(iframeRef.current)
      playerRef.current = player

      player.setCurrentTime(startTime).then(() => {
        player.play()
      })

      const checkTime = () => {
        player.getCurrentTime().then((currentTime: number) => {
          const timeUntilEnd = endTime - currentTime

          if (timeUntilEnd <= fadeThreshold && timeUntilEnd > 0) {
            const fadeProgress = timeUntilEnd / fadeThreshold
            setOpacity(fadeProgress)
          } else if (currentTime >= endTime) {
            player.setCurrentTime(startTime).then(() => {
              setOpacity(0)
              setTimeout(() => setOpacity(1), 50)
            })
          } else {
            setOpacity(1)
          }
        })
      }

      interval = setInterval(checkTime, 50)
    })

    return () => {
      mounted = false
      if (interval) clearInterval(interval)
      if (playerRef.current) {
        playerRef.current.destroy()
      }
    }
  }, [isInView, videoId, startTime, endTime])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative' }}
    >
      {isInView ? (
        <iframe
          ref={iframeRef}
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1&loop=1&muted=1&background=1&controls=0&dnt=1`}
          className="w-full h-full"
          style={{
            opacity,
            transition: 'opacity 0.3s ease-in-out',
          }}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          title="Abertura 180º"
        />
      ) : (
        <div className="w-full h-full bg-muted animate-pulse" />
      )}
    </div>
  )
}

export default LazyVimeoEmbed
